import { SAVE_CHANGES_FAILED } from 'bits/confirmation_dialog'
import { captureException } from 'bits/error_tracker'
import { __ } from 'bits/intl'
import PromiseQueue from 'bits/promise_queue'
import uniqueId from 'lodash.uniqueid'
import PadletApi from 'surface/padlet_api'
import { RootState } from 'surface/xstore/types'
import { Cid, Id, Post, PostAttributes } from 'types'
import Vue from 'vue'
import { Module } from 'vuex'

const HEADER_OFFSET = 1
const ADD_BUTTON_OFFSET = 1
const ZERO_INDEX_OFFSET = 1

type Coord = number
type RowCoord = Coord
type ColCoord = Coord
interface CellCoords {
  row: RowCoord
  col: ColCoord
}

interface CellData extends CellCoords {
  key: string
  id?: number
  isBlank?: boolean
  isOccupied?: boolean
  isPlaceholder?: boolean
  isHeader?: boolean
  isRowHeader?: boolean
  isColHeader?: boolean
  isCornerHeader?: boolean
  isPost?: boolean
  isUploading?: boolean
  isLastPost?: boolean
  isHeaderAddColButton?: boolean
  isHeaderAddRowButton?: boolean
  isAddColButton?: boolean
  isAddRowButton?: boolean
  isAddRowAndColButton?: boolean
  post?: Post
  text?: string | null
  numeral?: string
}

type RowCid = Cid
type ColCid = Cid
interface LateralData {
  id?: Id
  cid?: RowCid | ColCid
  title?: string | null
}
interface RowData extends LateralData {
  cid: RowCid
}
interface ColData extends LateralData {
  cid: ColCid
}

interface TableState {
  isLayoutFetched: boolean
  rowOrdering: RowData[]
  colOrdering: ColData[]
  activeCell: CellCoords | null
  cellBeingMoved: CellData | null
  headerCellBeingEdited: CellData | null
  openHeaderActionMenuKey: string | null
  requestQueue: PromiseQueue
}

const flatten = (arr: any[]): any[] => [].concat(...arr)
const rowCid = ({ id, cid }: { id?: Id; cid?: RowCid } = {}): RowCid => {
  if (cid) return cid
  if (id) return `row_${id}`
  return uniqueId('row_new_')
}
const colCid = ({ id, cid }: { id?: Id; cid?: ColCid } = {}): ColCid => {
  if (cid) return cid
  if (id) return `col_${id}`
  return uniqueId('col_new_')
}

interface IdToCoordMap {
  [id: number]: Coord
}
function createIdToCoordMap(ordering: RowData[] | ColData[]): IdToCoordMap {
  return ordering.reduce((hash, item, index): {} => {
    if (item.id) {
      hash[item.id] = index + HEADER_OFFSET
    }
    return hash
  }, {})
}
interface CidToCoordMap {
  [cid: string]: Coord
}
function createCidToCoordMap(ordering: RowData[] | ColData[]): CidToCoordMap {
  return ordering.reduce((hash, item, index): {} => {
    hash[item.cid] = index + HEADER_OFFSET
    return hash
  }, {})
}

const defaultState = (): TableState => ({
  isLayoutFetched: false,
  rowOrdering: [],
  colOrdering: [],
  activeCell: null,
  cellBeingMoved: null,
  headerCellBeingEdited: null,
  openHeaderActionMenuKey: null,
  requestQueue: new PromiseQueue(),
})

const TableModule: Module<TableState, RootState> = {
  namespaced: true,
  state: defaultState,
  getters: {
    /**********************
     * COORDS/ORDERING/ID/CID HELPERS
     **********************/
    cellToPostAttributes(state): (cell: CellData) => PostAttributes {
      return (cell): PostAttributes => {
        const rowData = state.rowOrdering[cell.row - HEADER_OFFSET]
        const colData = state.colOrdering[cell.col - HEADER_OFFSET]
        return {
          row_id: rowData.id,
          col_id: colData.id,
          rowCid: rowData.cid,
          colCid: colData.cid,
        }
      }
    },
    rowIdToCoordMap(state): IdToCoordMap {
      return createIdToCoordMap(state.rowOrdering)
    },
    colIdToCoordMap(state): IdToCoordMap {
      return createIdToCoordMap(state.colOrdering)
    },
    rowCidToCoordMap(state): CidToCoordMap {
      return createCidToCoordMap(state.rowOrdering)
    },
    colCidToCoordMap(state): CidToCoordMap {
      return createCidToCoordMap(state.colOrdering)
    },
    rowCidToId(state, getters): any {
      return (cid: RowCid): Id | undefined => state.rowOrdering[getters.rowCidToCoordMap[cid] - HEADER_OFFSET].id
    },
    colCidToId(state, getters): any {
      return (cid: ColCid): Id | undefined => state.colOrdering[getters.colCidToCoordMap[cid] - HEADER_OFFSET].id
    },
    rowIdToCid(state, getters): any {
      return (id: Id): RowCid | null => {
        const rowData = state.rowOrdering[getters.rowIdToCoordMap[id] - HEADER_OFFSET]
        return rowData ? rowData.cid : null
      }
    },
    colIdToCid(state, getters): any {
      return (id: Id): RowCid | null => {
        const colData = state.colOrdering[getters.colIdToCoordMap[id] - HEADER_OFFSET]
        return colData ? colData.cid : null
      }
    },
    getPostCoords(state, getters): any {
      return (post: PostAttributes): CellCoords | null => {
        if (!state.isLayoutFetched) return null
        let row
        let col
        if (post.row_id) {
          row = getters.rowIdToCoordMap[post.row_id]
        }
        if (post.col_id) {
          col = getters.colIdToCoordMap[post.col_id]
        }
        if (!row && post.rowCid) {
          row = getters.rowCidToCoordMap[post.rowCid]
        }
        if (!col && post.colCid) {
          col = getters.colCidToCoordMap[post.colCid]
        }
        return row && col ? { row, col } : null
      }
    },
    hasAssignedCell(state, getters): any {
      return (post: PostAttributes): boolean => !!getters.getPostCoords(post)
    },
    hasNoAssignedCell(state, getters): any {
      return (post: PostAttributes): boolean => !getters.getPostCoords(post)
    },

    posts(state, getters, rootState, rootGetters): Post[] {
      return rootGetters.isTable && state.isLayoutFetched ? rootGetters['post/postsArray'] : []
    },
    pendingPosts(state, getters, rootState, rootGetters): Post[] {
      return rootGetters.isTable && state.isLayoutFetched ? rootGetters['post/pendingPosts'] : []
    },
    activeCell(state): CellCoords | null {
      return state.activeCell
    },
    isActiveCell(state): any {
      return (cell: CellCoords): boolean => {
        if (!state.activeCell) return false
        return cell.row === state.activeCell.row && cell.col === state.activeCell.col
      }
    },
    cellBeingMoved(state): CellData | null {
      return state.cellBeingMoved
    },
    rowBeingMoved(state): Coord | null {
      const isRowBeingMoved = state.cellBeingMoved && state.cellBeingMoved.isRowHeader
      return isRowBeingMoved ? (state.cellBeingMoved as CellData).row : null
    },
    colBeingMoved(state): Coord | null {
      const isColBeingMoved = state.cellBeingMoved && state.cellBeingMoved.isColHeader
      return isColBeingMoved ? (state.cellBeingMoved as CellData).col : null
    },
    postBeingMoved(state, getters): Post | null {
      return (getters.cellBeingMoved && getters.cellBeingMoved.post) || null
    },
    isPostBeingMoved(state, getters): boolean {
      return !!getters.postBeingMoved
    },
    headerCellBeingEdited(state): CellData | null {
      return state.headerCellBeingEdited
    },
    celledCreatedPosts(state, getters): Post[] {
      return getters.posts.filter(getters.hasAssignedCell)
    },
    celllessCreatedPosts(state, getters): Post[] {
      return getters.posts.filter(getters.hasNoAssignedCell)
    },
    celllessPendingPosts(state, getters): Post[] {
      return getters.pendingPosts.filter(getters.hasNoAssignedCell)
    },
    postsPendingUpload(state, getters): Post[] {
      return getters.pendingPosts.filter(getters.hasAssignedCell)
    },
    isSelectingCellForPendingPost(state, getters): boolean {
      return getters.celllessPendingPosts.length > 0
    },
    isSelectingCellForCreatedPost(state, getters): boolean {
      return !getters.isSelectingCellForPendingPost && getters.celllessCreatedPosts.length > 0
    },
    isSelectingCell(state, getters): boolean {
      return getters.isPostBeingMoved || getters.isSelectingCellForPendingPost || getters.isSelectingCellForCreatedPost
    },
    postToSelectCellFor(state, getters): Post | null {
      if (getters.isPostBeingMoved) return getters.postBeingMoved
      if (getters.isSelectingCellForPendingPost) return getters.celllessPendingPosts[0]
      if (getters.isSelectingCellForCreatedPost) return getters.celllessCreatedPosts[0]
      return null
    },
    rows(state): number {
      return state.rowOrdering.length
    },
    cols(state): number {
      return state.colOrdering.length
    },
    lastRowIndex(state): number {
      // - ZERO_INDEX_OFFSET + HEADER_OFFSET + ADD_BUTTON_OFFSET = 1
      return state.rowOrdering.length + 1
    },
    lastColIndex(state): number {
      // - ZERO_INDEX_OFFSET + HEADER_OFFSET + ADD_BUTTON_OFFSET = 1
      return state.colOrdering.length + 1
    },
    isPostContentHidden(state, getters, rootState, rootGetters): (post: PostAttributes) => boolean {
      const canUserViewPost = rootGetters['post/canUserViewPost']
      return (post: PostAttributes): boolean => {
        if (post.is_content_hidden) return true
        if (canUserViewPost(post)) return false
        return true
      }
    },
    cells(state, getters): CellData[] {
      if (!state.isLayoutFetched) return []

      const totalRows = HEADER_OFFSET + getters.rows + ADD_BUTTON_OFFSET
      const totalCols = HEADER_OFFSET + getters.cols + ADD_BUTTON_OFFSET
      const contentMatrix: CellData[][] = Array(totalRows)
        .fill(null)
        .map((_r, row): CellData[] =>
          Array(totalCols)
            .fill(null)
            .map((_c, col): CellData => ({ row, col, key: `placeholder-${row}-${col}`, isPlaceholder: true })),
        )

      const posts = getters.celledCreatedPosts
      const getPostCoords = getters.getPostCoords
      const isPostContentHidden = getters.isPostContentHidden
      const contentHiddenPosts = posts.filter(isPostContentHidden)
      const contentVisiblePosts = posts.filter((post): boolean => !isPostContentHidden(post))

      contentVisiblePosts.forEach((post: Post, index): void => {
        const { row, col } = getPostCoords(post)
        const isLastPost = index === posts.length - 1
        contentMatrix[row][col] = { row, col, key: `post-${post.cid}`, isPost: true, post, isLastPost }
      })
      contentHiddenPosts.forEach((post: Post): void => {
        const { row, col } = getPostCoords(post)
        contentMatrix[row][col] = { row, col, key: `post-${post.cid}`, isOccupied: true, post }
      })
      getters.postsPendingUpload.forEach((post: Post): void => {
        const { row, col } = getPostCoords(post)
        contentMatrix[row][col] = { row, col, key: `uploading-${row}-${col}`, isUploading: true, post }
      })

      // Populate row and col header cells
      const { rowOrdering, colOrdering } = state
      Array(getters.rows)
        .fill(null)
        .forEach((_, index): void => {
          const { id, title } = rowOrdering[index]
          const row = HEADER_OFFSET + index
          const col = 0
          const numeral = row.toString()
          const text = id ? title : undefined
          contentMatrix[row][col] = {
            id,
            row,
            col,
            key: `header-${row}-${col}`,
            isHeader: true,
            isRowHeader: true,
            text,
            numeral,
          }
        })
      Array(getters.cols)
        .fill(null)
        .forEach((_, index): void => {
          const { id, title } = colOrdering[index]
          const row = 0
          const col = HEADER_OFFSET + index
          const numeral = String.fromCharCode(65 + index)
          const text = id ? title : undefined
          contentMatrix[row][col] = {
            id,
            row,
            col,
            key: `header-${row}-${col}`,
            isHeader: true,
            isColHeader: true,
            text,
            numeral,
          }
        })

      const lastRow = totalRows - ZERO_INDEX_OFFSET
      const lastCol = totalCols - ZERO_INDEX_OFFSET
      contentMatrix[0][0] = { row: 0, col: 0, isCornerHeader: true, key: 'corner-0-0' }
      Array(getters.rows + ADD_BUTTON_OFFSET)
        .fill(null)
        .forEach((_, index): void => {
          const row = HEADER_OFFSET + index
          const col = lastCol
          contentMatrix[row][col] = { row, col, key: `add-col-${row}-${col}`, isAddColButton: true }
        })
      Array(getters.cols)
        .fill(null)
        .forEach((_, index): void => {
          const row = lastRow
          const col = HEADER_OFFSET + index
          contentMatrix[row][col] = { row: lastRow, col, key: `add-row-${row}-${col}`, isAddRowButton: true }
        })

      const addColButtonData = { row: 0, col: lastCol, isHeaderAddColButton: true, key: 'header-add-col' }
      const addRowButtonData = { row: lastRow, col: 0, isHeaderAddRowButton: true, key: 'header-add-row' }
      const addRowAndColButtonData = { row: lastRow, col: lastCol, isAddRowAndColButton: true, key: 'add-row-and-col' }
      contentMatrix[0][addColButtonData.col] = addColButtonData
      contentMatrix[addRowButtonData.row][0] = addRowButtonData
      contentMatrix[addRowButtonData.row][addColButtonData.col] = addRowAndColButtonData

      return flatten(contentMatrix)
    },
    canCancelSelectCell(state, getters): boolean {
      return getters.isSelectingCellForPendingPost
    },
    canRearrangeTable(state, getters, rootState, rootGetters): boolean {
      return rootGetters.isPostable
    },
    isHeaderActionMenuOpen(state): (headerKey: string) => boolean {
      return (headerKey): boolean => state.openHeaderActionMenuKey === headerKey
    },
    isKeyboardNavigationDisabled(state, getters, rootState, rootGetters): boolean {
      return !!(state.openHeaderActionMenuKey || state.cellBeingMoved) || rootGetters.isAPanelVisible
    },
    isPristine(state, getters): boolean {
      return getters.rows === 1 && getters.cols === 1 && getters.posts.length === 0 && getters.pendingPosts.length === 0
    },
  },
  mutations: {
    UPDATE_LAYOUT(state, { rowOrdering, colOrdering }): void {
      state.rowOrdering = rowOrdering
      state.colOrdering = colOrdering
      state.isLayoutFetched = true
    },
    ADD_NEW_ROW(state, { at, cid }): void {
      const newRow = { cid: cid || rowCid() }
      state.rowOrdering =
        typeof at !== 'number'
          ? [...state.rowOrdering, newRow]
          : [...state.rowOrdering.slice(0, at), newRow, ...state.rowOrdering.slice(at)]
    },
    ADD_NEW_COL(state, { at, cid }): void {
      const newCol = { cid: cid || colCid() }
      state.colOrdering =
        typeof at !== 'number'
          ? [...state.colOrdering, newCol]
          : [...state.colOrdering.slice(0, at), newCol, ...state.colOrdering.slice(at)]
    },
    UPDATE_ROW_TITLE(state, { id, title }): void {
      const row = state.rowOrdering.find((rowData): boolean => rowData.id === id)
      if (row) Vue.set(row, 'title', title)
    },
    UPDATE_COL_TITLE(state, { id, title }): void {
      const col = state.colOrdering.find((colData): boolean => colData.id === id)
      if (col) Vue.set(col, 'title', title)
    },
    UPDATE_ROW_ID(state, { cid, id }: RowData): void {
      const row = state.rowOrdering.find((rowData): boolean => rowData.cid === cid)
      if (row) Vue.set(row, 'id', id)
    },
    UPDATE_COL_ID(state, { cid, id }: ColData): void {
      const col = state.colOrdering.find((colData): boolean => colData.cid === cid)
      if (col) Vue.set(col, 'id', id)
    },
    UPDATE_ROW_POSITION(state, { cid, layoutIndex }): void {
      const originalIndex = state.rowOrdering.findIndex((row): boolean => row.cid === cid)
      const newOrdering = [...state.rowOrdering]
      newOrdering.splice(originalIndex, 1)
      newOrdering.splice(layoutIndex, 0, state.rowOrdering[originalIndex])
      state.rowOrdering = newOrdering
    },
    UPDATE_COL_POSITION(state, { cid, layoutIndex }): void {
      const originalIndex = state.colOrdering.findIndex((col): boolean => col.cid === cid)
      const newOrdering = [...state.colOrdering]
      newOrdering.splice(originalIndex, 1)
      newOrdering.splice(layoutIndex, 0, state.colOrdering[originalIndex])
      state.colOrdering = newOrdering
    },
    SET_ACTIVE_CELL(state, cell: CellCoords): void {
      state.activeCell = cell
    },
    UNSET_ACTIVE_CELL(state): void {
      state.activeCell = null
    },
    START_MOVING_CELL(state, cell: CellData): void {
      state.cellBeingMoved = cell
    },
    STOP_MOVING_CELL(state): void {
      state.cellBeingMoved = null
    },
    START_EDITING_HEADER(state, cell: CellData): void {
      state.headerCellBeingEdited = cell
    },
    STOP_EDITING_HEADER(state): void {
      state.headerCellBeingEdited = null
    },
    OPEN_HEADER_ACTION_MENU(state, headerKey): void {
      state.openHeaderActionMenuKey = headerKey
    },
    CLOSE_HEADER_ACTION_MENU(state): void {
      state.openHeaderActionMenuKey = null
    },
  },
  actions: {
    /**********************
     * UPDATE LAYOUT
     **********************/
    async fetchTableLayout({ dispatch, rootState }): Promise<any> {
      try {
        const payload = { wall: { id: rootState.wall.id } }
        const layoutInArray = await PadletApi.WallTableLayout.readAll(payload)
        dispatch('updateLayout', layoutInArray[0])
      } catch (e) {
        captureException(e)
      }
    },
    updateLayout({ commit, state, getters }, { rows, cols, row_titles: rowTitles, col_titles: colTitles }): void {
      // Use the same Cid for the whole session
      const rowOrdering = rows.map(
        (id: Id): RowData => ({ id, cid: getters.rowIdToCid(id) || rowCid({ id }), title: rowTitles[id] }),
      )
      const colOrdering = cols.map(
        (id: Id): ColData => ({ id, cid: getters.colIdToCid(id) || colCid({ id }), title: colTitles[id] }),
      )
      // Inject unpersisted rows/cols
      state.rowOrdering.forEach((rowData, index): void => {
        if (rowData.id) return
        const destinationIndex = Math.min(index, rowOrdering.length)
        rowOrdering.splice(destinationIndex, 0, rowData)
      })
      state.colOrdering.forEach((colData, index): void => {
        if (colData.id) return
        const destinationIndex = Math.min(index, colOrdering.length)
        colOrdering.splice(destinationIndex, 0, colData)
      })
      commit('UPDATE_LAYOUT', { rowOrdering, colOrdering })
    },

    /**********************
     * CREATE ROW/COLS
     **********************/
    updateRowId({ commit, dispatch, getters }, { cid, id }): void {
      commit('UPDATE_ROW_ID', { cid, id })
      const updatePostRowId = (post): Promise<any> =>
        dispatch('post/savePostAttributes', { ...post, row_id: id }, { root: true })
      const postsToUpdate = getters.celledCreatedPosts.filter((post): boolean => !post.row_id && post.rowCid === cid)
      postsToUpdate.forEach(updatePostRowId)
    },
    updateColId({ commit, dispatch, getters }, { cid, id }): void {
      commit('UPDATE_COL_ID', { cid, id })
      const updatePostColId = (post): Promise<any> =>
        dispatch('post/savePostAttributes', { ...post, col_id: id }, { root: true })
      const postsToUpdate = getters.celledCreatedPosts.filter((post): boolean => !post.col_id && post.colCid === cid)
      postsToUpdate.forEach(updatePostColId)
    },
    async addTableRow({ commit, dispatch, state, rootState }, payload = {}): Promise<any> {
      const cid = rowCid()
      const newRowPayload: { cid: RowCid; at?: Coord } = { cid }
      const requestPayload: any = { wall_id: rootState.wall.id }
      if (typeof payload.at === 'number') {
        const at = payload.at - HEADER_OFFSET
        requestPayload.at = at
        newRowPayload.at = at
      }
      commit('ADD_NEW_ROW', newRowPayload)
      try {
        const createRow = (): Promise<any> => PadletApi.WallTableLayout.createRow(requestPayload)
        const { layout, new_row: newRow } = await state.requestQueue.enqueue('addTableRow', createRow)
        dispatch('updateRowId', { cid, id: newRow.id })
        // Update layout in case our 'best effort' reconciliation for unpersisted rows/cols is out of sync
        dispatch('updateLayout', layout)
      } catch (e) {
        captureException(e)
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
      }
    },
    async addTableCol({ commit, dispatch, state, rootState }, payload = {}): Promise<any> {
      const cid = colCid()
      const newColPayload: { cid: ColCid; at?: Coord } = { cid }
      const requestPayload: any = { wall_id: rootState.wall.id }
      if (typeof payload.at === 'number') {
        const at = payload.at - HEADER_OFFSET
        requestPayload.at = at
        newColPayload.at = at
      }
      commit('ADD_NEW_COL', newColPayload)
      try {
        const createCol = (): Promise<any> => PadletApi.WallTableLayout.createCol(requestPayload)
        const { layout, new_col: newCol } = await state.requestQueue.enqueue('addTableCol', createCol)
        dispatch('updateColId', { cid, id: newCol.id })
        // Update layout in case our 'best effort' reconciliation for unpersisted rows/cols is out of sync
        dispatch('updateLayout', layout)
      } catch (e) {
        captureException(e)
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
      }
    },
    async addTableRowAndCol({ commit, dispatch, state, rootState }): Promise<any> {
      const newRowCid = rowCid()
      const newColCid = colCid()
      commit('ADD_NEW_ROW', { cid: newRowCid })
      commit('ADD_NEW_COL', { cid: newColCid })

      try {
        const requestPayload: any = { wall_id: rootState.wall.id }
        const createRowAndCol = (): Promise<any> => PadletApi.WallTableLayout.createRowAndCol(requestPayload)
        const { layout, new_row: newRow, new_col: newCol } = await state.requestQueue.enqueue(
          'addTableRowAndCol',
          createRowAndCol,
        )
        dispatch('updateRowId', { cid: newRowCid, id: newRow.id })
        dispatch('updateColId', { cid: newColCid, id: newCol.id })
        // Update layout in case our 'best effort' reconciliation for unpersisted rows/cols is out of sync
        dispatch('updateLayout', layout)
      } catch (e) {
        captureException(e)
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
      }
    },

    /**********************
     * REMOVE COLUMN
     **********************/
    removeTableCol({ state, getters, dispatch }, { col }): void {
      const { id, cid } = state.colOrdering[col - HEADER_OFFSET]
      const isPostInCol = (post: PostAttributes): boolean => post.col_id === id || post.colCid === cid
      const postsToDelete = getters.celledCreatedPosts.filter(isPostInCol)
      const hasPost = postsToDelete.length > 0
      const payload = { id, cid, postsToDelete }

      if (hasPost) {
        dispatch('confirmRemoveTableCol', payload)
      } else {
        dispatch('actuallyRemoveTableCol', payload)
      }
    },
    confirmRemoveTableCol({ dispatch }, payload): void {
      const confirmationOptions = {
        title: __('This column has one or more posts'),
        body: __('The posts will be deleted with the column. Continue?'),
        confirmButtonText: __('Delete Posts'),
        cancelButtonText: __('Keep working'),
        afterConfirmActions: [{ type: 'table/actuallyRemoveTableCol', payload }],
      }
      dispatch('confirmationDialog/openConfirmationDialog', confirmationOptions, { root: true })
    },
    async actuallyRemoveTableCol({ dispatch, state, getters }, { id, cid, postsToDelete }): Promise<any> {
      const deleteCol = (): Promise<any> => {
        const idToDelete = id || getters.colCidToId(cid)
        if (!idToDelete) return Promise.resolve()
        return PadletApi.WallTableLayout.deleteCol({ id: idToDelete })
      }
      try {
        const layout = await state.requestQueue.enqueue('actuallyRemoveTableCol', deleteCol)
        postsToDelete.forEach((post: Post): void => {
          dispatch('post/removePost', post, { root: true })
        })
        dispatch('updateLayout', layout)
      } catch (e) {
        captureException(e)
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
      }
    },

    /**********************
     * REMOVE ROW
     **********************/
    removeTableRow({ state, getters, dispatch }, { row }): void {
      const { id, cid } = state.rowOrdering[row - HEADER_OFFSET]
      const isPostInRow = (post: PostAttributes): boolean => post.row_id === id || post.rowCid === cid
      const postsToDelete = getters.celledCreatedPosts.filter(isPostInRow)
      const hasPost = postsToDelete.length > 0
      const payload = { id, cid, postsToDelete }

      if (hasPost) {
        dispatch('confirmRemoveTableRow', payload)
      } else {
        dispatch('actuallyRemoveTableRow', payload)
      }
    },
    confirmRemoveTableRow({ dispatch }, payload): void {
      const confirmationOptions = {
        title: __('This row has one or more posts'),
        body: __('The posts will be deleted with the row. Continue?'),
        confirmButtonText: __('Delete Posts'),
        cancelButtonText: __('Keep working'),
        afterConfirmActions: [{ type: 'table/actuallyRemoveTableRow', payload }],
      }
      dispatch('confirmationDialog/openConfirmationDialog', confirmationOptions, { root: true })
    },
    async actuallyRemoveTableRow({ dispatch, state, getters }, { id, cid, postsToDelete }): Promise<any> {
      const deleteRow = (): Promise<any> => {
        const idToDelete = id || getters.rowCidToId(cid)
        if (!idToDelete) return Promise.resolve()
        return PadletApi.WallTableLayout.deleteRow({ id: idToDelete })
      }

      try {
        const layout = await state.requestQueue.enqueue('actuallyRemoveTableRow', deleteRow)
        postsToDelete.forEach((post: Post): void => {
          dispatch('post/removePost', post, { root: true })
        })
        dispatch('updateLayout', layout)
      } catch (e) {
        captureException(e)
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
      }
    },

    /**********************
     * CELL OPERATIONS
     **********************/
    startNewPostInCell({ dispatch, getters }, cell: CellData): void {
      dispatch('post/startNewPost', { attributes: getters.cellToPostAttributes(cell) }, { root: true })
    },
    async startNewPostInFringeCell({ dispatch }, cell: CellData): Promise<any> {
      let createCellsAction = ''
      if (cell.isAddRowButton) {
        createCellsAction = 'addTableRow'
      }
      if (cell.isAddColButton) {
        createCellsAction = 'addTableCol'
      }
      if (cell.isAddRowAndColButton) {
        createCellsAction = 'addTableRowAndCol'
      }
      if (!createCellsAction) return

      dispatch(createCellsAction)
      dispatch('startNewPostInCell', cell)
    },
    cancelSelectCellForPendingPost({ dispatch, getters }): void {
      const post = getters.celllessPendingPosts.length > 0 && getters.celllessPendingPosts[0]
      if (post) dispatch('post/removePendingPost', post, { root: true })
    },
    cancelSelectCell({ dispatch, getters }): void {
      if (getters.isSelectingCellForPendingPost) dispatch('cancelSelectCellForPendingPost')
    },
    putPendingPostInCell({ dispatch, getters }, { post: { cid }, cell }): void {
      dispatch('post/updatePendingPost', { cid, ...getters.cellToPostAttributes(cell) }, { root: true })
    },
    // Ensures that post has been created before we try to update it.
    async enqueuePutPostInCell({ dispatch, getters }, { post, cell }): Promise<any> {
      const cellIds = getters.cellToPostAttributes(cell)
      const method = (latestPost: Post): Promise<any> => dispatch('putPostInCell', { post: latestPost, cell, cellIds })
      dispatch('post/enqueueRequest', { post, customRequest: { key: 'PUT_POST_IN_CELL', method } }, { root: true })
    },
    async putPostInCell({ dispatch, state, getters }, { post, cell, cellIds }): Promise<any> {
      const putPostInCell = (): Promise<any> => {
        // Row/cols might have shifted, so don't rely on cell.row/cell.col if possible
        const rowCoord = getters.rowCidToCoordMap[cellIds.rowCid]
        const colCoord = getters.colCidToCoordMap[cellIds.colCid]
        const rowIndex = (typeof rowCoord === 'number' ? rowCoord : cell.row) - HEADER_OFFSET
        const colIndex = (typeof colCoord === 'number' ? colCoord : cell.col) - HEADER_OFFSET
        const payload = { wish_id: post.id, row_index: rowIndex, col_index: colIndex }
        return PadletApi.WallTableLayout.putPostInCell(payload)
      }
      try {
        const layout = await state.requestQueue.enqueue('putPostInCell', putPostInCell)
        dispatch('updateLayout', layout)
        // TODO: make optimistic
        const updatedPost = {
          ...post,
          row_id: state.rowOrdering[cell.row - HEADER_OFFSET].id,
          col_id: state.colOrdering[cell.col - HEADER_OFFSET].id,
        }
        dispatch('post/updatePostInStore', updatedPost, { root: true })
      } catch (e) {
        captureException(e)
      }
    },
    selectCell({ dispatch, getters }, cell: CellData): void {
      const post = getters.postToSelectCellFor as Post
      if (getters.isPostBeingMoved) dispatch('enqueuePutPostInCell', { post, cell })
      if (getters.isSelectingCellForPendingPost) dispatch('putPendingPostInCell', { post, cell })
      if (getters.isSelectingCellForCreatedPost) dispatch('enqueuePutPostInCell', { post, cell })
    },
    startMovingCell({ commit }, cell: CellData): void {
      commit('START_MOVING_CELL', cell)
    },
    stopMovingCell({ commit }): void {
      commit('STOP_MOVING_CELL')
    },

    /**********************
     * CELL NAVIGATION
     **********************/
    setActiveCell({ commit, getters }, cell: CellCoords): void {
      if (!getters.isActiveCell(cell)) {
        commit('SET_ACTIVE_CELL', cell)
      }
    },
    unsetActiveCell({ commit }): void {
      commit('UNSET_ACTIVE_CELL')
    },
    navigateToCellCoords({ dispatch, getters }, { row, col }: CellCoords): void {
      const cell = getters.cells.find((c: CellData): boolean => c.row === row && c.col === col)
      dispatch('setActiveCell', cell)

      // TODO: return if no permissions
      if (cell.isPost) {
        dispatch('post/startEditingPost', { postCid: (cell.post as Post).cid }, { root: true })
      } else if (cell.isHeader) {
        dispatch('startEditingHeader', cell)
      } else if (cell.isPlaceholder) {
        dispatch('startNewPostInCell', cell)
      }
    },
    stepNextCell({ dispatch, getters }, options = {}): boolean {
      if (getters.isKeyboardNavigationDisabled) return false

      const activeCell = getters.activeCell
      if (!activeCell && !options.stepToCellIfNoneSelected) return false

      dispatch('post/stopEditingPost', null, { root: true })

      // If enabled, step into first cell
      if (!activeCell) {
        dispatch('navigateToCellCoords', { col: 1, row: 0 })
        return true
      }

      // Step right
      const { row, col } = activeCell as CellCoords
      if (col < getters.lastColIndex) {
        const newCol = col + 1
        dispatch('navigateToCellCoords', { col: newCol, row })
        return true
      }

      // Step into next row
      if (row < getters.lastRowIndex) {
        dispatch('navigateToCellCoords', { col: 0, row: row + 1 })
        return true
      }

      // Allow focus to exit table
      dispatch('unsetActiveCell')
      return false
    },
    stepPreviousCell({ dispatch, getters }, options = {}): boolean {
      if (getters.isKeyboardNavigationDisabled) return false

      const activeCell = getters.activeCell
      if (!activeCell && !options.stepToCellIfNoneSelected) return false

      dispatch('post/stopEditingPost', null, { root: true })

      // If enabled, step into last cell
      if (!activeCell) {
        dispatch('navigateToCellCoords', { col: getters.lastColIndex, row: getters.lastRowIndex })
        return true
      }

      // Step left
      const { row, col } = activeCell as CellCoords
      if (col !== 0) {
        const newCol = col - 1
        dispatch('navigateToCellCoords', { col: newCol, row })
        return true
      }

      // Step into previous row
      if (row !== 0) {
        dispatch('navigateToCellCoords', { col: getters.lastColIndex, row: row - 1 })
        return true
      }

      // Allow focus to exit table
      dispatch('unsetActiveCell')
      return false
    },
    stepLeftCell({ dispatch, state, getters, rootGetters }): boolean {
      const actionDisabled =
        !getters.activeCell ||
        getters.isKeyboardNavigationDisabled ||
        rootGetters['post/postBeingEditedCid'] ||
        state.headerCellBeingEdited
      if (actionDisabled) return false

      const { row, col } = state.activeCell as CellCoords
      if (col - 1 < 0) return false
      dispatch('setActiveCell', { col: col - 1, row })
      return true
    },
    stepRightCell({ dispatch, state, getters, rootGetters }): boolean {
      const actionDisabled =
        !getters.activeCell ||
        getters.isKeyboardNavigationDisabled ||
        rootGetters['post/postBeingEditedCid'] ||
        state.headerCellBeingEdited
      if (actionDisabled) return false

      const { row, col } = state.activeCell as CellCoords
      if (col + 1 > getters.cols + 1) return false
      dispatch('setActiveCell', { col: col + 1, row })
      return true
    },
    stepAboveCell({ dispatch, state, getters, rootGetters }): boolean {
      const actionDisabled =
        !getters.activeCell || getters.isKeyboardNavigationDisabled || rootGetters['post/postBeingEditedCid']
      if (actionDisabled) return false

      const { row, col } = state.activeCell as CellCoords
      if (row - 1 < 0) return false

      dispatch('stopEditingHeader')
      dispatch('setActiveCell', { row: row - 1, col })
      return true
    },
    stepBelowCell({ dispatch, state, getters, rootGetters }): boolean {
      const actionDisabled =
        !getters.activeCell || getters.isKeyboardNavigationDisabled || rootGetters['post/postBeingEditedCid']
      if (actionDisabled) return false

      const { row, col } = state.activeCell as CellCoords
      if (row + 1 > getters.rows + 1) return false

      dispatch('stopEditingHeader')
      dispatch('setActiveCell', { row: row + 1, col })
      return true
    },
    enterActiveCell({ dispatch, state, getters, rootGetters }): boolean {
      const actionDisabled =
        !getters.activeCell ||
        getters.isKeyboardNavigationDisabled ||
        rootGetters['post/postBeingEditedCid'] ||
        state.headerCellBeingEdited
      if (actionDisabled) return false

      const { row, col } = state.activeCell as CellCoords
      const cell = getters.cells.find((c: CellData): boolean => c.row === row && c.col === col)

      if (cell.isBlank) {
        return false
      }
      if (cell.isPost) {
        dispatch('post/startEditingPost', { postCid: (cell.post as Post).cid }, { root: true })
        return true
      }
      if (cell.isPlaceholder) {
        dispatch('startNewPostInCell', cell)
        return true
      }
      if (cell.isHeader) {
        dispatch('startEditingHeader', cell)
        return true
      }
      if (cell.isHeaderAddRowButton) {
        dispatch('addTableRow')
        return true
      }
      if (cell.isHeaderAddColButton) {
        dispatch('addTableCol')
        return true
      }
      if (cell.isAddRowButton || cell.isAddColButton || cell.isAddRowAndColButton) {
        dispatch('startNewPostInFringeCell', cell)
        return true
      }
      return false
    },

    /**********************
     * ROW/COL HEADER OPERATIONS
     **********************/
    async updateTableRowTitle({ dispatch, state, getters }, { id, cid, title }): Promise<any> {
      const updateRow = (): Promise<any> => {
        const rowId = id || getters.rowCidToId[cid]
        if (!rowId) return Promise.resolve()
        return PadletApi.WallTableLayout.updateRow({ id: rowId, title })
      }
      try {
        const layout = await state.requestQueue.enqueue('updateTableRowTitle', updateRow)
        dispatch('updateLayout', layout)
      } catch (e) {
        captureException(e)
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
      }
    },
    async updateTableColTitle({ dispatch, state, getters }, { id, cid, title }): Promise<any> {
      const updateCol = (): Promise<any> => {
        const colId = id || getters.colCidToId[cid]
        if (!colId) return Promise.resolve()
        return PadletApi.WallTableLayout.updateCol({ id: colId, title })
      }
      try {
        const layout = await state.requestQueue.enqueue('updateTableColTitle', updateCol)
        dispatch('updateLayout', layout)
      } catch (e) {
        captureException(e)
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
      }
    },
    async moveTableRow({ commit, dispatch, state, getters }, { sourceIndex, indexToMoveAfter }): Promise<any> {
      const destinationIndex = indexToMoveAfter + (indexToMoveAfter < sourceIndex ? 1 : 0)
      if (sourceIndex === destinationIndex) return

      const { id, cid } = state.rowOrdering[sourceIndex - HEADER_OFFSET]
      const layoutIndex = destinationIndex - HEADER_OFFSET
      commit('UPDATE_ROW_POSITION', { cid, layoutIndex })

      const updateRow = (): Promise<any> => {
        const rowId = id || getters.rowCidToId[cid]
        if (!rowId) return Promise.resolve()
        return PadletApi.WallTableLayout.updateRow({ id: rowId, layout_index: layoutIndex })
      }

      try {
        await state.requestQueue.enqueue('moveTableRow', updateRow)
      } catch (e) {
        captureException(e)
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
      }
    },
    async moveTableCol({ commit, dispatch, state, getters }, { sourceIndex, indexToMoveAfter }): Promise<any> {
      const destinationIndex = indexToMoveAfter + (indexToMoveAfter < sourceIndex ? 1 : 0)
      if (sourceIndex === destinationIndex) return

      const { id, cid } = state.colOrdering[sourceIndex - HEADER_OFFSET]
      const layoutIndex = destinationIndex - HEADER_OFFSET
      commit('UPDATE_COL_POSITION', { cid, layoutIndex })

      const updateCol = (): Promise<any> => {
        const colId = id || getters.colCidToId[cid]
        if (!colId) return Promise.resolve()
        return PadletApi.WallTableLayout.updateCol({ id: colId, layout_index: layoutIndex })
      }

      try {
        await state.requestQueue.enqueue('moveTableCol', updateCol)
      } catch (e) {
        captureException(e)
        dispatch('snackbars/setSnackbar', SAVE_CHANGES_FAILED, { root: true })
      }
    },
    startEditingHeader({ commit }, cell): void {
      commit('START_EDITING_HEADER', cell)
    },
    stopEditingHeader({ commit, state }, cell): void {
      if (!state.headerCellBeingEdited) return
      if (cell && state.headerCellBeingEdited && cell.key !== state.headerCellBeingEdited.key) return
      commit('STOP_EDITING_HEADER')
    },
    openHeaderActionMenu({ commit }, headerKey): void {
      commit('OPEN_HEADER_ACTION_MENU', headerKey)
    },
    closeHeaderActionMenu({ commit, state }, headerKey): void {
      if (state.openHeaderActionMenuKey === headerKey) {
        commit('CLOSE_HEADER_ACTION_MENU')
      }
    },

    /**********************
     * REMOTE UPDATES
     **********************/
    editLayoutRemote({ dispatch }, layout): any {
      dispatch('updateLayout', layout)
    },
    editRowRemote({ commit }, { id, title }): any {
      commit('UPDATE_ROW_TITLE', { id, title })
    },
    editColRemote({ commit }, { id, title }): any {
      commit('UPDATE_COL_TITLE', { id, title })
    },
  },
}
export default TableModule
export { TableState, CellData }
